import pino from 'pino';
import * as Sentry from '@sentry/react';

/**
 * Log Levels:
 * - debug: Detailed debugging information (dev only)
 * - info: General informational messages
 * - warn: Warning messages that don't stop execution
 * - error: Error messages that need attention
 */
export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

/**
 * Structured logger for Brainy.ai
 * 
 * Features:
 * - Multiple log levels with automatic filtering
 * - Structured logging with context objects
 * - Sentry integration for errors and warnings
 * - Pretty printing in development
 * - Production-ready JSON output
 * 
 * Usage:
 * - logger.debug({ query, results }, 'Database query executed')
 * - logger.info({ userId, action }, 'User uploaded PDF')
 * - logger.warn({ userId, attempt }, 'Rate limit approaching')
 * - logger.error({ error, context }, 'Failed to process PDF')
 */
export const logger = pino({
  level: import.meta.env.DEV ? 'debug' : 'info',
  browser: {
    asObject: true,
    serialize: true,
    transmit: {
      level: 'warn', // Send warn and error to Sentry
      send: (level, logEvent) => {
        // Send errors and warnings to Sentry in all environments
        if (level === 'error' || level === 'warn') {
          const { messages, bindings } = logEvent;
          const [context, message] = messages;
          
          // Determine Sentry severity level
          const sentryLevel: Sentry.SeverityLevel = level === 'error' ? 'error' : 'warning';
          
          // Send to Sentry with context
          Sentry.withScope((scope) => {
            // Add all context as extra data
            if (context && typeof context === 'object') {
              Object.entries(context).forEach(([key, value]) => {
                scope.setExtra(key, value);
              });
              
              // If there's an error object, capture it as exception
              if ('error' in context && context.error instanceof Error) {
                scope.setLevel(sentryLevel);
                Sentry.captureException(context.error);
                return;
              }
            }
            
            // Otherwise capture as message
            scope.setLevel(sentryLevel);
            Sentry.captureMessage(message || 'Log message', sentryLevel);
          });
          
          // Also log to console for immediate visibility in dev
          if (import.meta.env.DEV) {
            const consoleMethod = level === 'error' ? console.error : console.warn;
            consoleMethod('[' + level.toUpperCase() + ']', message, context);
          }
        }
      },
    },
  },
  transport: import.meta.env.DEV ? {
    target: 'pino-pretty',
    options: {
      colorize: true,
      translateTime: 'HH:MM:ss',
      ignore: 'pid,hostname',
      singleLine: false,
    },
  } : undefined,
});

/**
 * Convenience methods for common logging patterns
 */
export const log = {
  /** Log user actions */
  userAction: (userId: string, action: string, metadata?: Record<string, any>) => {
    logger.info({ userId, action, ...metadata }, `User action: ${action}`);
  },

  /** Log API calls */
  apiCall: (endpoint: string, method: string, status: number, duration: number) => {
    logger.info({ endpoint, method, status, duration }, `API ${method} ${endpoint}`);
  },

  /** Log AI operations */
  aiOperation: (operation: string, provider: string, tokens?: number, metadata?: Record<string, any>) => {
    logger.info({ operation, provider, tokens, ...metadata }, `AI operation: ${operation}`);
  },

  /** Log database queries */
  dbQuery: (query: string, table: string, duration: number) => {
    logger.debug({ query, table, duration }, `DB query: ${table}`);
  },

  /** Log errors with context */
  error: (error: Error, context?: Record<string, any>) => {
    logger.error({ 
      error: {
        message: error.message,
        stack: error.stack,
        name: error.name,
      },
      ...context 
    }, `Error: ${error.message}`);
  },

  /** Log performance metrics */
  performance: (metric: string, value: number, unit: string = 'ms') => {
    logger.info({ metric, value, unit }, `Performance: ${metric}`);
  },

  /** Log security events */
  security: (event: string, severity: 'low' | 'medium' | 'high', metadata?: Record<string, any>) => {
    logger.warn({ event, severity, ...metadata }, `Security: ${event}`);
  },
};

export default logger;
